"use client";

import { motion } from "motion/react";

import { cn } from "@/lib/utils";

import { EASE_IN_OUT, EASE_OUT_EXPO } from "../motion/reveal";
import { useUncovered } from "../motion/stage";

// =============================================================================

type PageTitleProps = {
  /** One string per line: ["ALL MY", "CREATIONS"] */
  lines: string[];
  className?: string;
  as?: "h1" | "h2";
  /** Animate on mount (page heading) or when scrolled into view */
  immediate?: boolean;
  delay?: number;
  /** Coral square dot after the last line */
  withDot?: boolean;
};

/**
 * Heavy uppercase title ending with the coral square dot. Each line rises
 * from behind a mask, then the dot pops in.
 */
export function PageTitle({
  lines,
  className,
  as = "h1",
  immediate = true,
  delay = 0.15,
  withDot = true,
}: PageTitleProps) {
  const Tag = motion[as];
  const uncovered = useUncovered();
  const trigger = immediate
    ? { animate: uncovered ? "visible" : "hidden" }
    : uncovered && {
        whileInView: "visible",
        viewport: { once: true, margin: "0px 0px -10% 0px" },
      };

  return (
    <Tag
      initial="hidden"
      {...trigger}
      transition={{ staggerChildren: 0.09, delayChildren: delay }}
      className={cn("text-display", className)}
      aria-label={lines.join(" ")}
    >
      <PageTitleLines lines={lines} withDot={withDot} />
    </Tag>
  );
}

// =============================================================================

/**
 * Masked lines of a title, animated by the "hidden" / "visible" variants of
 * the parent heading. The dot stays glued to the last word so it never wraps
 * alone on a narrow screen.
 */
export function PageTitleLines({
  lines,
  withDot,
}: {
  lines: string[];
  withDot: boolean;
}) {
  return lines.map((line, index) => {
    const isLast = index === lines.length - 1;
    const split = line.lastIndexOf(" ") + 1;
    return (
      <span
        key={`${line}-${index}`}
        aria-hidden
        className="block overflow-hidden pb-[0.06em]"
      >
        <motion.span
          className="inline-block"
          variants={{
            hidden: { y: "105%", rotate: 2 },
            visible: {
              y: "0%",
              rotate: 0,
              transition: { duration: 1, ease: EASE_IN_OUT },
            },
          }}
        >
          {isLast && withDot ? (
            <>
              {line.slice(0, split)}
              <span className="whitespace-nowrap">
                {line.slice(split)}
                <motion.span
                  className="ml-[0.04em] inline-block size-[0.19em] bg-brand align-baseline"
                  variants={{
                    hidden: { scale: 0, opacity: 0 },
                    visible: {
                      scale: 1,
                      opacity: 1,
                      transition: {
                        delay: 0.65,
                        duration: 0.6,
                        ease: EASE_OUT_EXPO,
                      },
                    },
                  }}
                />
              </span>
            </>
          ) : (
            line
          )}
        </motion.span>
      </span>
    );
  });
}

// =============================================================================
